import React, { useContext, useEffect, useRef } from "react";
// Importăm VideoJS și plugin-ul HLS
import videojs from "video.js/dist/video.js";
import "@videojs/http-streaming";
// Importăm doar CSS-ul de bază
import "video.js/dist/video-js.css";

import DispatchContext from "../DispatchContext";
import StateContext from "../StateContext";

const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

const VideoPlayer = ({ options, onPlayerReady }) => {
  const videoRef = useRef(null);
  const playerRef = useRef(null);

  const dispatch = useContext(DispatchContext);
  const appState = useContext(StateContext);

  useEffect(() => {
    if (videoRef.current) {
      if (!playerRef.current) {
        // Configurăm playerul cu opțiunile optimizate pentru HLS
        const videoOptions = {
          ...options,
          inactivityTimeout: 0,
          liveui: false,
          html5: {
            vhs: {
              overrideNative: !isSafari, // Safari = false, restul true
              enableLowInitialPlaylist: true,
              limitRenditionByPlayerDimensions: true,
              smoothQualityChange: true,
              bandwidth: 5000000,
              backBufferLength: 30,
              bufferBasedABR: true,
              experimentalBufferBasedABR: true,
              experimentalLeastPixelDiffSelector: true,
              maxPlaylistRetries: 5,
              maxPlaylistRetryDelay: 10000,
              maxSegmentRetries: 3,
              maxSegmentRetryDelay: 5000,
              maxBufferLength: 30,
              maxBufferSize: 60 * 1000 * 1000,
              maxBufferHole: 0.5,
              liveTolerance: 15,
              liveTracker: {
                trackingThreshold: 0,
                liveTolerance: 15
              },
              withCredentials: false,
              xhrSetup: function(xhr, url) {
                xhr.withCredentials = false;
              }
            },
            nativeAudioTracks: false,
            nativeVideoTracks: false
          },
          liveui: false,
          liveTracker: {
            trackingThreshold: 0,
            liveTolerance: 15
          }
        };
        
        playerRef.current = videojs(videoRef.current, videoOptions, () => {
          if (onPlayerReady) {
            onPlayerReady(playerRef.current);
            dispatch({ type: "playerReady" });
          }
        });

        // Event listeners pentru gestionarea erorilor și debugging
        playerRef.current.on("playing", () => {
          dispatch({ type: "playerPlaying" });
        });
        playerRef.current.on("error", () => {
          const errorDetails = playerRef.current.error();
          if (errorDetails && errorDetails.code === 3) {
            setTimeout(() => {
              if (playerRef.current && playerRef.current.tech_ && playerRef.current.tech_.vhs) {
                const vhs = playerRef.current.tech_.vhs;
                if (vhs.playlists && vhs.playlists.length > 1) {
                  const currentPlaylist = vhs.playlists.media();
                  const lowerQualityPlaylist = vhs.playlists.filter(p => p.attributes.BANDWIDTH < currentPlaylist.attributes.BANDWIDTH)[0];
                  if (lowerQualityPlaylist) {
                    vhs.playlists.media(lowerQualityPlaylist);
                  }
                }
              }
            }, 1000);
          } else {
            setTimeout(() => {
              if (playerRef.current && options.sources) {
                playerRef.current.src(options.sources);
                playerRef.current.load();
              }
            }, 2000);
          }
        });
        playerRef.current.on("loadedmetadata", () => {});
        playerRef.current.on("canplay", () => {});
        playerRef.current.on("loadstart", () => {});
        playerRef.current.on("loadeddata", () => {});
        playerRef.current.on("waiting", () => {});
        playerRef.current.on("stalled", () => {});
        playerRef.current.on("usage", () => {});
        if (playerRef.current.tech_ && playerRef.current.tech_.vhs) {
          playerRef.current.tech_.vhs.on('usage', () => {});
          playerRef.current.tech_.vhs.on('playlistchange', () => {});
        }
      }
    }
    return () => {
      if (playerRef.current) {
        playerRef.current.dispose();
        playerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (playerRef.current) {
      playerRef.current.src(options.sources);
      playerRef.current.muted(options.muted);
    }
  }, [options.sources, options.muted]);

  return (
    <div
      className={`video-wrapper ${appState.isPlayerPlaying ? "visible" : ""}`}
    >
      <div data-vjs-player>
        <video ref={videoRef} className="video-js"></video>
      </div>
    </div>
  );
};

export default VideoPlayer;
